/**
 * StockInsightCard Component
 *
 * Displays a stock pick with price data, change percentage, and key metrics.
 * Used for "top_pick" and can be extended for other stock-based insights.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { InsightConfig } from '../quizTypes';
import type { FMPQuote } from '../../../services/fmpDataService';
import { formatCurrency } from '../../../utils/formatters';
import { typography } from '../../../common/styles/universalTheme';
import InsightCard from './InsightCard';

interface StockInsightCardProps {
  config: InsightConfig;
  quote: FMPQuote | null;
  isLoading: boolean;
  onNavigate: (route: string) => void;
}

const StockInsightCard: React.FC<StockInsightCardProps> = ({
  config,
  quote,
  isLoading,
  onNavigate,
}) => {
  const theme = useTheme();

  // Format market cap for display
  const formatMarketCap = (value: number): string => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return formatCurrency(value);
  };

  // Determine change color
  const changeColor =
    quote && quote.changesPercentage >= 0
      ? theme.palette.success.main
      : theme.palette.error.main;

  return (
    <InsightCard
      label={config.label}
      rationale={config.rationale}
      deepLinkRoute={config.deepLinkRoute}
      onNavigate={onNavigate}
      isLoading={isLoading}
    >
      {quote && (
        <Box>
          {/* Symbol and company name */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {quote.symbol}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {quote.name}
            </Typography>
          </Box>

          {/* Price and change */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.75 }}>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontWeight: 600,
                fontSize: typography.fontSize.lg,
                color: 'text.primary',
              }}
            >
              ${quote.price.toFixed(2)}
            </Typography>
            <Typography
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontVariantNumeric: 'tabular-nums lining-nums',
                fontSize: typography.fontSize.sm,
                fontWeight: 500,
                color: changeColor,
              }}
            >
              {quote.changesPercentage >= 0 ? '+' : ''}
              {quote.changesPercentage.toFixed(2)}%
            </Typography>
          </Box>

          {/* Key metrics */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {quote.pe && quote.pe > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                  P/E
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontFamily: typography.fontFamily.mono,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {quote.pe.toFixed(1)}
                </Typography>
              </Box>
            )}
            {quote.marketCap && quote.marketCap > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                  Market Cap
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontFamily: typography.fontFamily.mono,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'text.secondary',
                    fontWeight: 500,
                  }}
                >
                  {formatMarketCap(quote.marketCap)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </InsightCard>
  );
};

export default StockInsightCard;
